"use client";

import { useEffect } from "react";
import { initFlare, reportError } from "@/lib/flare";

/**
 * Last-resort boundary. Next owns the React root here, so the FlareErrorBoundary
 * component used in the Laravel apps has nowhere to mount; this file is the App
 * Router's equivalent and must render its own <html>/<body> because the failing
 * layout is exactly what it replaces.
 *
 * Without this, a render crash showed Next's default error page and reported
 * nothing.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // Initialise here rather than trusting FlareInit: when the very first
    // render throws, React discards that subtree before any effect commits, so
    // FlareInit never ran and reportError would no-op. That cold-load crash is
    // exactly the case this boundary exists for, so it cannot depend on a
    // sibling component having mounted successfully. initFlare is idempotent.
    initFlare(process.env.NEXT_PUBLIC_FLARE_KEY, {
      stage: process.env.NEXT_PUBLIC_APP_STAGE ?? "production",
      release: process.env.NEXT_PUBLIC_APP_RELEASE,
    });

    // A boundary swallows the error, so Flare's global handlers never see it.
    // The digest ties this to the server-side occurrence in the Lambda logs.
    reportError(error, {
      boundary: "global-error",
      ...(error.digest ? { digest: error.digest } : {}),
    });
  }, [error]);

  return (
    <html lang="nl">
      <body>
        <div
          style={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "1rem",
            fontFamily: "system-ui, sans-serif",
            background: "#f9fafb",
          }}
        >
          <div
            style={{
              maxWidth: "28rem",
              width: "100%",
              textAlign: "center",
              background: "#fff",
              border: "1px solid #e5e7eb",
              borderRadius: "0.5rem",
              padding: "2rem",
            }}
          >
            <h1 style={{ fontSize: "1.25rem", fontWeight: 600, color: "#111827" }}>
              Er ging iets mis
            </h1>
            <p
              style={{
                marginTop: "0.75rem",
                fontSize: "0.875rem",
                color: "#4b5563",
              }}
            >
              Deze pagina kon niet worden geladen. Probeer het opnieuw.
            </p>
            <button
              type="button"
              onClick={() => reset()}
              style={{
                marginTop: "1.5rem",
                width: "100%",
                background: "#2563eb",
                color: "#fff",
                border: "none",
                borderRadius: "0.375rem",
                padding: "0.5rem 1rem",
                fontSize: "0.875rem",
                fontWeight: 500,
                cursor: "pointer",
              }}
            >
              Probeer opnieuw
            </button>
          </div>
        </div>
      </body>
    </html>
  );
}
